// React & Hooks

import AirDatepicker, {
  type AirDatepickerLocale,
  type AirDatepickerOptions,
  type AirDatepickerPosition,
} from 'air-datepicker';
// Date & Locale
import localeEn from 'air-datepicker/locale/en';
import localeId from 'air-datepicker/locale/id';
import localeKo from 'air-datepicker/locale/ko';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/shared/components/ui/Button';
// UI Components
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
import Filtering from '@/shared/components/ui/Filtering/Filtering';
import GeneralInput from '@/shared/components/ui/GeneralInput/GeneralInput';
import { formatDateTime } from '@/shared/utils/date';
import 'air-datepicker/air-datepicker.css';

import UseStatistic from '@/domains/communication/hooks/useStatistic';
// Statistic Components & Hooks
import AllStatistic from '@/domains/statistic/components/AllStatistic';
import StatisticByLane from '@/domains/statistic/components/StatisticByLane';
import StatisticByType from '@/domains/statistic/components/StatisticByType';

const CommunicationStatistic = () => {
  const { i18n } = useTranslation();
  const [_isResetClicked, setIsResetClicked] = useState(false);
  const today = new Date();
  const midnight = new Date(new Date().setHours(0, 0, 0, 0));
  const [activeButton, setActiveButton] = useState('5분');
  const [interval, setInterval] = useState(5);
  const [dateTime, setDateTime] = useState({
    start_date: formatDateTime(midnight),
    end_date: formatDateTime(today),
  });

  const handleButtonClick = (label: string, newInterval: number) => {
    setActiveButton(label);
    setInterval(newInterval);
    const { start_date, end_date } = dateTime;
    const dateParams: string[] = [];
    if (start_date) dateParams.push(`start_time=${start_date}`);
    if (end_date) dateParams.push(`end_time=${end_date}`);
    const result = [...dateParams, `interval=${newInterval}`, 'order=desc']
      .filter(Boolean)
      .join('&');
    setQueryParams(result);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setDateTime((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  useEffect(() => {
    let locale: AirDatepickerLocale;
    if (i18n.language === 'eng') {
      locale = localeEn;
    } else if (i18n.language === 'ind') {
      locale = localeId;
    } else {
      locale = localeKo;
    }
    const today = new Date();
    const midnight = new Date(new Date().setHours(0, 0, 0, 0));

    const optionsDate: AirDatepickerOptions = {
      autoClose: true,
      locale: locale,
      position: 'bottom center' as unknown as AirDatepickerPosition,
      selectedDates: [midnight],
      timepicker: true,
      timeFormat: 'HH:mm',
      dateFormat: 'yyyy-MM-dd',
      onSelect: (date) => {
        setDateTime((prevValues) => ({
          ...prevValues,
          start_date: date.formattedDate as string,
        }));
      },
    };

    const optionsDateSecond: AirDatepickerOptions = {
      autoClose: true,
      locale: locale,
      position: 'bottom center' as unknown as AirDatepickerPosition,
      timepicker: true,
      timeFormat: 'HH:mm',
      dateFormat: 'yyyy-MM-dd',
      selectedDates: [today],
      onSelect: (date) => {
        setDateTime((prevValues) => ({
          ...prevValues,
          end_date: date.formattedDate as string,
        }));
      },
    };

    const datepicker1 = new AirDatepicker('[name="start_date"]', optionsDate);
    const datepicker2 = new AirDatepicker('[name="end_date"]', optionsDateSecond);
    setIsResetClicked(false);
    return () => {
      datepicker1.destroy();
      datepicker2.destroy();
    };
  }, [i18n.language]);

  const [activeTab, setActiveTab] = useState(0);
  const [queryParams, setQueryParams] = useState(
    `start_time=${formatDateTime(midnight)}&end_time=${formatDateTime(today)}&interval=${interval}&order=desc`
  );

  const {
    objectTrafficList,
    objectCntList,
    objectCntLaneList,
    objectTrafficChartList,
    objectCntChartList,
    objectCntLaneChartList,
  } = UseStatistic({
    queryParams: queryParams,
  });

  const objectTraffic = objectTrafficList?.data?.flat();
  const objectCnt = objectCntList?.data?.flat();
  const objectCntLane = objectCntLaneList?.data?.flat();
  const objectTrafficChart = objectTrafficChartList?.data?.flat();
  const objectCntChart = objectCntChartList?.data?.flat();
  const objectCntLaneChart = objectCntLaneChartList?.data?.flat();

  const handleReset = () => {
    setIsResetClicked(true);
    setDateTime({
      start_date: formatDateTime(midnight),
      end_date: formatDateTime(today),
    });
    setActiveButton('5분');
    setInterval(5);
  };

  const handleSearch = useCallback(
    (inputVal = '') => {
      const resultInput = inputVal ? `input=${inputVal}` : '';
      const { start_date, end_date } = dateTime;
      const dateParams: string[] = [];
      if (start_date) {
        dateParams.push(`start_time=${start_date}`);
      }

      if (end_date) {
        dateParams.push(`end_time=${end_date}`);
      }
      const intervalParam = interval ? `interval=${interval}` : '';
      const result = [resultInput, ...dateParams, intervalParam, 'order=desc']
        .filter(Boolean)
        .join('&');
      setQueryParams(result);
    },
    [dateTime, interval]
  );

  const tabs = [
    {
      label: '전체 통계',
      content: <AllStatistic data={objectTraffic} chartData={objectTrafficChart} />,
    },
    {
      label: '차종별 통계',
      content: <StatisticByType data={objectCnt} chartData={objectCntChart} />,
    },
    {
      label: '차로별 통계',
      content: <StatisticByLane data={objectCntLane} chartData={objectCntLaneChart} />,
    },
  ];

  return (
    <section className="wrap">
      <div className="header-title">
        <h3>통계</h3>
        <h3>&gt;</h3>
        <h3>소통 정보</h3>
      </div>

      <ContainerCard>
        <Filtering
          placeholder="사이트명, 접근로명"
          customWidthInput=" flex xl:flex-[0.8] lg:flex-1 md:flex-1 "
          showLabel={false}
          onSearch={handleSearch}
          onReset={handleReset}
          disableFiltering={true}
          customWidthSelect="w-full flex flex-1 basis-[700px]"
        >
          {/* <Filtering 
            placeholder="사이트 / 접근로"
            customWidthInput=" flex flex-1 w-full!"
            showLabel= {false} 
            disableFiltering={true} 
            customWidthSelect= "w-full flex flex-1 basis-[790px] "> */}

          <div className="flex w-fit gap-2 flex-row items-center">
            <span className="text-sm text-gray-500 whitespace-nowrap">간격</span>
            <Button
              customButton={`button filtering cursor-pointer hover:opacity-80 ${
                activeButton === '5분' ? 'active' : ''
              }`}
              label="5분"
              onClick={() => handleButtonClick('5분', 5)}
            />
            <Button
              customButton={`button filtering cursor-pointer hover:opacity-80 ${
                activeButton === '15분' ? 'active' : ''
              }`}
              label="15분"
              onClick={() => handleButtonClick('15분', 15)}
            />
            <Button
              customButton={`button filtering cursor-pointer hover:opacity-80 ${
                activeButton === '30분' ? 'active' : ''
              }`}
              label="30분"
              onClick={() => handleButtonClick('30분', 30)}
            />
            <Button
              customButton={`button filtering cursor-pointer hover:opacity-80 ${
                activeButton === '60분' ? 'active' : ''
              }`}
              label="60분"
              onClick={() => handleButtonClick('60분', 60)}
            />
          </div>
          <div className="flex flex-row gap-2 items-center w-full">
            <GeneralInput
              isDob={true}
              type="text"
              name={'start_date'}
              value={dateTime.start_date}
              onChange={handleInputChange}
            />
            <span>-</span>
            <GeneralInput
              isDob={true}
              type="text"
              name={'end_date'}
              value={dateTime.end_date}
              onChange={handleInputChange}
            />
          </div>
        </Filtering>
      </ContainerCard>

      <div className="w-full">
        <div className="flex flex-row gap-4 bg-[#E6E6E6] dark:bg-[#1a2535] rounded-tl-lg rounded-tr-lg">
          {tabs.map((tab, index) => (
            <button
              type="button"
              key={tab.label}
              className={`w-fit py-2 text-center cursor-pointer hover:opacity-80 ${
                activeTab === index
                  ? 'bg-[#FEFEFE] dark:bg-[#1e3043] text-[#135A78] dark:text-[#9cc8dc] font-bold px-4 rounded-tl-lg rounded-tr-lg border-t border-l border-r border-[#E6E6E6] dark:border-[#2b465f] text-[14px]'
                  : 'border-transparent text-[#6A6A6A] dark:text-[#808890] px-4 text-[14px]'
              }`}
              onClick={() => setActiveTab(index)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="bg-[#fff] dark:bg-[#1a2030] p-[20px] border-r border-l border-b border-[#E6E6E6] dark:border-[#2b465f] rounded-bl-[10px] rounded-br-[10px]">
          {tabs[activeTab]?.content}
        </div>
      </div>
    </section>
  );
};

export default CommunicationStatistic;
